import siteLogo from "../../assets/images/site-logo-small.avif";
import siteLogoSvg from "../../assets/images/site-logo-small-svg.svg";
import header_footer_json from "../../utils/shared/header-footer-nav-items-v2.json";
import NavItemV2 from "./nav-itemV2";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type Dispatch, type SetStateAction } from "react";
import { MaintenanceMessageContent } from "../../models/polymer/MaintenanceMessage.model";
import HeaderAlertV2 from "../header-alert/header-alertV2";
import axios from "axios";
import { Helpers } from "../../utils/helper";
import { API_WarningAlert } from "../../utils/api";
import { useAppSelector } from '../../redux/hook';

const HEADER_BREAKPOINT_PX = 1199;
const useIsNarrowViewport = () => {
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth <= HEADER_BREAKPOINT_PX);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${HEADER_BREAKPOINT_PX}px)`);
    const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    setIsNarrow(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return isNarrow;
};



function HeaderV2({
  isHidden,
  setIsHidden,
}: {
  isHidden: boolean;
  setIsHidden: Dispatch<SetStateAction<boolean>>;
}) {
    const isNarrowViewport = useIsNarrowViewport();
    const navItem = header_footer_json["navigation-items"];
    const [showHeaderAlertContent, setShowHeaderAlertContent] = useState<boolean>(false);
    const [maintenanceMsgList, setMaintenanceMsgList] = useState<MaintenanceMessageContent[]>([]);
    const [showSiteSearch, setShowSiteSearch] = useState<boolean>(false);
    const [hideMobileNavSearch, setHideMobileNavSearch] = useState(false);
    const [hasMaintenanceMsg, setHasMaintenanceMsg] = useState<boolean>(false);
    const isAuthenticated = useAppSelector(state => state.auth.AuthenticationSlice.isAuthenticated);
    const isSingPass = useAppSelector(state => state.auth.AuthenticationSlice.isSingPass);
    const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);
    const alertContainerRef = useRef<HTMLDivElement>(null);
    const [alertBannerHeightPx, setAlertBannerHeightPx] = useState(0);

    useLayoutEffect(() => {
      const el = alertContainerRef.current;
      if (!el || !showHeaderAlertContent) {
        setAlertBannerHeightPx(0);
        return;
      }
      const measure = () => setAlertBannerHeightPx(el.offsetHeight);
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }, [showHeaderAlertContent, maintenanceMsgList]);

    const callWarningAlertApi = useCallback(async () => {
      try {
        const response = await axios.get(`${API_WarningAlert}`);
        if (response.status === 200) {
          console.log("Maintenance Message List", response);
          setHasMaintenanceMsg(response.data.length > 0);
          const contentList = response.data.map((data: any) => {
            const title = data.type;
            const content = data.msg.toString();
            const currentTime = new Date();
            const endTime = Helpers.parseTimestamp(data.timestamp);
            const startTime = new Date(data.startDate);
            
            if(currentTime.getTime() > startTime.getTime() && currentTime.getTime() < endTime.getTime()){        
              return new MaintenanceMessageContent({title, content});
            }else{
              return null;  
            }
          });
          
          setMaintenanceMsgList(contentList.filter((item: MaintenanceMessageContent | null): item is MaintenanceMessageContent => item !== null));
        }
      } catch (e) {
        // alert("Opps, something wrong when getting warning alert.");
        console.log("Maintenance Message List Error", e);
        setHasMaintenanceMsg(false);
      }
    },[showHeaderAlertContent]);

    useEffect(() => {
      callWarningAlertApi();
    }, []);

    const handleOnClick = useCallback(() => {
      if(isSingPass){
        window.location.href = "/transfer/logout?spToken=" + singpassToken;
      }else{
        console.error("enter else");
        Helpers.clearAuthPersistData();
        Helpers.clearPersistData();
        window.location.href = "/transfer";
      }
    }, [isSingPass, singpassToken])

    /* Mobile menu uses isHidden to hide app-main; reset when leaving narrow viewport or content stays display:none */
    useEffect(() => {
      if (!isNarrowViewport) {
        setIsHidden(false);
      }
    }, [isNarrowViewport, setIsHidden]);

    useEffect(() => {
      if (!isHidden) setHideMobileNavSearch(false);
    }, [isHidden]);

    const mobileMenuSearchOpen = isNarrowViewport && isHidden && !hideMobileNavSearch;
    const desktopSearchOpen = !isNarrowViewport && showSiteSearch;
    const searchVisible = desktopSearchOpen || mobileMenuSearchOpen;

    return (
      <header
        className="header-v2-root"
        style={{ "--header-v2-alert-offset": `${alertBannerHeightPx}px` } as CSSProperties}
      >
        <div ref={alertContainerRef} className="old-header-alert-container">
          {
            showHeaderAlertContent && (
              <HeaderAlertV2 headerAlertContent={maintenanceMsgList} onClose={()=>{setShowHeaderAlertContent(false)}} />
            )
          }
        </div>
        <div className="header-v2-container">
          <div className="header-v2-logo">
            <a href="https://www.hdb.gov.sg/cs/infoweb/homepage" aria-label="hdb-home">
              {isNarrowViewport ? (
                <img width={124} height={30.1953} src={siteLogoSvg} alt="Housing & Development Board" className="polymer-site-logo" />
              ) : (
                <img width={200} height={49} src={siteLogoSvg} alt="Housing & Development Board" className="polymer-site-logo" />
              )}
            </a>
          </div>
          <div className="header-v2-nav">
            <NavItemV2
              navItem={navItem}
              setShowHeaderAlertContent={setShowHeaderAlertContent}
              showHeaderAlertContent={showHeaderAlertContent}
              setShowSiteSearch={setShowSiteSearch}
              showSiteSearch={showSiteSearch}
              hasMaintenanceMsg={hasMaintenanceMsg}
              isAuth={isAuthenticated}
              handleOnClick={handleOnClick}
              isNarrowViewport={isNarrowViewport}
              isHidden={isHidden}
              setIsHidden={setIsHidden}
              hideMobileNavSearch={hideMobileNavSearch}
              setHideMobileNavSearch={setHideMobileNavSearch}
            />
          </div>
        </div>
        {/* Single #searchsg-searchbar: desktop row vs narrow fixed over mobile menu (no duplicate id). */}
        <div
          className={`site-search header-v2-site-search${searchVisible ? " header-v2-site-search--open" : ""}${mobileMenuSearchOpen ? " header-v2-site-search--narrow-fixed" : ""}`}
          hidden={!searchVisible}
          aria-hidden={!searchVisible}
        >
          <div className="wrapper-margin">
            <div id="searchsg-searchbar" />
          </div>
        </div>
      </header>
    );
}
export default HeaderV2;